import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Check, CircleCheck, History, Pencil, Plus, Trash2, TriangleAlert, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { FieldError } from '@/components/ui/field'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { MarkdownViewer } from '@/components/MarkdownViewer'
import { TestCasePriorities, TestCaseTypes } from '@/types/testCase'
import { changeSetItemTitle } from '@/lib/changeSetItemTitle'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage, getValidationErrorMessage } from '@/lib/validationErrors'
import { cn } from '@/lib/utils'
import { wordDiff } from '@/lib/wordDiff'
import type { WorkflowChangeSet, WorkflowChangeSetItem, ChangeSetItemDecision, ChangeSetEntityKind } from '@/api/workflowChangeSets'
import { Badge } from '@/components/ui/badge'

type DiffView = 'split' | 'unified' | 'final'

const UNSAVED_CHANGES_WARNING = 'Save or cancel your changes before switching.'

// Mirrors WorkflowChangeSetConstants.EditableFieldsFor; the server rejects anything else.
const EDITABLE_FIELDS: Record<ChangeSetEntityKind, ReadonlySet<string>> = {
  Intent: new Set(['title', 'description', 'epic', 'feature', 'technicalApproach']),
  WorkItem: new Set(['title', 'description', 'epic', 'feature', 'technicalApproach']),
  TestCase: new Set(['title', 'description', 'type', 'priority', 'steps']),
  TestPlan: new Set(),
  // Title omitted: DocumentChangeSetHandler never applies it.
  Document: new Set(['content']),
}

const REQUIRED_FIELDS: Record<ChangeSetEntityKind, ReadonlySet<string>> = {
  Intent: new Set(['title', 'description']),
  WorkItem: new Set(['title', 'description']),
  TestCase: new Set(['title']),
  TestPlan: new Set(),
  Document: new Set(['content']),
}

function choicesFor(entityKind: ChangeSetEntityKind, fieldKey: string): readonly string[] | undefined {
  if (entityKind !== 'TestCase') return undefined
  if (fieldKey === 'type') return Object.values(TestCaseTypes)
  if (fieldKey === 'priority') return Object.values(TestCasePriorities)
  return undefined
}

const MULTILINE_FIELDS = new Set(['description', 'technicalApproach', 'content'])

// Prose fields only, so a '#' in a title or status is not rendered as a heading.
const MARKDOWN_FIELDS = new Set(['description', 'technicalApproach', 'content'])

function isEditableField(kind: ChangeSetEntityKind, fieldKey: string): boolean {
  return EDITABLE_FIELDS[kind]?.has(fieldKey) ?? false
}

const ENTITY_KIND_LABELS: Record<ChangeSetEntityKind, string> = {
  Intent: 'Intent',
  WorkItem: 'Work item',
  TestCase: 'Test case',
  TestPlan: 'Test plan',
  Document: 'Document',
}

interface ChangeSetDiffDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  changeSet: WorkflowChangeSet
  initialItemId?: string | null
  canDecide: boolean
  canEdit: boolean
  onSetDecision: (itemId: string, decision: ChangeSetItemDecision, options?: { onError?: (error: unknown) => void }) => void
  onSaveProposedContent: (itemId: string, proposedValuesJson: string, options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) => void
  isSaving?: boolean
}

interface StepDiffItem {
  stepNumber: number
  description: string
  expectedResult: string | null
  // Keys the editor does not model, preserved so editing a step never drops them.
  raw: Record<string, unknown>
}

/** Null when the value cannot be read as a step array, so callers can tell it from empty. */
function parseSteps(value: string | null): StepDiffItem[] | null {
  if (!value) return []
  try {
    const parsed = JSON.parse(value) as unknown
    if (!Array.isArray(parsed)) return null
    if (parsed.some((s) => s === null || typeof s !== 'object')) return null
    return (parsed as Record<string, unknown>[]).map((s, i) => ({
      stepNumber: typeof s['stepNumber'] === 'number' ? s['stepNumber'] : i + 1,
      description: typeof s['description'] === 'string' ? s['description'] : '',
      expectedResult: typeof s['expectedResult'] === 'string' ? s['expectedResult'] : null,
      raw: s,
    }))
  } catch {
    return null
  }
}

type FieldConfig = {
  order: string[]
  hidden: Set<string>
}

const DEFAULT_FIELD_CONFIG: FieldConfig = {
  order: ['title', 'description', 'epic', 'feature', 'technicalApproach', 'status'],
  hidden: new Set(),
}

const FIELD_CONFIG: Partial<Record<ChangeSetEntityKind, FieldConfig>> = {
  TestCase: {
    order: ['title', 'description', 'type', 'priority', 'steps'],
    hidden: new Set(['testSuiteId', 'workItemId']),
  },
  Document: {
    order: ['content'],
    // Title mirrors the content's first heading and is already shown in the header.
    hidden: new Set(['title', 'fileName', 'sourceType', 'description']),
  },
}

function configFor(kind: ChangeSetEntityKind): FieldConfig {
  return FIELD_CONFIG[kind] ?? DEFAULT_FIELD_CONFIG
}

function parseValues(json: string | null): Record<string, string | null> {
  if (!json) return {}
  try {
    const parsed = JSON.parse(json) as unknown
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    // Value kinds are not guaranteed by the contract, so coerce rather than trust them.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).map(([key, value]) => [
        key,
        value === null || value === undefined ? null : typeof value === 'string' ? value : String(value),
      ]),
    )
  } catch {
    return {}
  }
}

function orderedFields(item: WorkflowChangeSetItem): { key: string; before: string | null; after: string | null }[] {
  const cfg = configFor(item.entityKind)
  const proposed = parseValues(item.proposedValuesJson)
  const before = parseValues(item.beforeValuesJson)
  const keys = [...new Set([...cfg.order.filter((k) => k in proposed), ...Object.keys(proposed)])]
    .filter((k) => !cfg.hidden.has(k))
  return keys.map((key) => ({
    key,
    before: item.operation === 'Update' ? (before[key] ?? null) : null,
    after: proposed[key] ?? null,
  }))
}

function DiffText({ before, after, side }: { before: string; after: string; side: 'before' | 'after' | 'unified' }) {
  const segments = useMemo(() => wordDiff(before, after), [before, after])
  return (
    <span className="whitespace-pre-wrap break-words">
      {segments.map((seg, idx) => {
        if (side === 'before' && seg.type === 'add') return null
        if (side === 'after' && seg.type === 'del') return null
        if (seg.type === 'add')
          return (
            <span key={idx} className="rounded-sm px-0.5">
              {seg.text}
            </span>
          )
        if (seg.type === 'del')
          return (
            <span key={idx} className="rounded-sm px-0.5 text-danger-foreground line-through">
              {seg.text}
            </span>
          )
        return <span key={idx}>{seg.text}</span>
      })}
    </span>
  )
}

function FieldDiff({ field, view, isCreate }: {
  field: { key: string; before: string | null; after: string | null }
  view: DiffView
  isCreate: boolean
}) {
  const label = field.key.replace(/([A-Z])/g, ' $1')
  const testId = `change-set-field-${field.key}`

  if (view === 'final') {
    return (
      <div className="mt-3" data-testid={testId}>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="rounded-md border px-3 py-2 text-sm leading-relaxed break-words">
          {!field.after
            ? <span className="italic text-muted-foreground">—</span>
            : MARKDOWN_FIELDS.has(field.key)
              ? <MarkdownViewer content={field.after} bodyTextClass="text-foreground" />
              : <span className="whitespace-pre-wrap break-words">{field.after}</span>}
        </div>
      </div>
    )
  }

  if (view === 'split') {
    return (
      <div className="mt-3" data-testid={testId}>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="grid grid-cols-2 overflow-hidden rounded-md border">
          <div className="border-r bg-muted px-3 py-2">
            <div className="mb-1 text-[11px] font-bold tracking-wider">Current *</div>
            <div className="text-sm leading-relaxed">
              {isCreate || field.before === null
                ? <span className="italic text-muted-foreground"> Does not exist </span>
                : <DiffText before={field.before} after={field.after ?? ''} side="before" />}
            </div>
          </div>
          <div className="bg-success px-3 py-2">
            <div className="mb-1 text-[11px] font-bold tracking-wider">Proposed *</div>
            <div className="text-sm leading-relaxed">
              {isCreate || field.before === null
                ? <span className="rounded-sm px-0.5 whitespace-pre-wrap break-words">{field.after ?? ''}</span>
                : <DiffText before={field.before} after={field.after ?? ''} side="after" />}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3" data-testid={testId}>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="rounded-md border px-3 py-2 text-sm leading-relaxed">
        {isCreate || field.before === null
          ? <span className="rounded-sm px-0.5 whitespace-pre-wrap break-words">{field.after ?? ''}</span>
          : <DiffText before={field.before} after={field.after ?? ''} side="unified" />}
      </div>
    </div>
  )
}

function isDocumentContent(item: WorkflowChangeSetItem, fieldKey: string) {
  return item.entityKind === 'Document' && fieldKey === 'content'
}

function DocumentContentDiff({ value }: { value: string | null }) {
  return (
    <div className="mt-3" data-testid="change-set-document-content">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Document</div>
      {value
        ? (
          <div className="rounded-md border bg-success/10 px-4 py-3">
            <MarkdownViewer content={value} bodyTextClass="text-foreground" />
          </div>
        )
        : <div className="rounded-md border px-3 py-2 text-sm italic text-muted-foreground">No document content.</div>}
    </div>
  )
}

function TestStepsFieldDiff({ value }: { value: string | null }) {
  const steps = parseSteps(value)

  if (steps === null) {
    return (
      <div className="mt-3" data-testid="change-set-field-steps">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Steps</div>
        <div className="rounded-md border px-3 py-2 text-sm italic text-danger-foreground">
          Steps could not be read.
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3" data-testid="change-set-field-steps">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Steps</div>
      {steps.length === 0 ? (
        <div className="rounded-md border px-3 py-2 text-sm italic text-muted-foreground">No test steps.</div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table aria-label="Steps">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">#</TableHead>
                <TableHead>Step Name</TableHead>
                <TableHead>Expected Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {steps.map((step) => (
                <TableRow key={step.stepNumber}>
                  <TableCell className="align-top text-muted-foreground">{step.stepNumber}</TableCell>
                  <TableCell className="align-top whitespace-pre-wrap">
                    {step.description}
                  </TableCell>
                  <TableCell className="align-top whitespace-pre-wrap">
                    {step.expectedResult ?? ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function AutoSizingTextarea({ id, ariaLabel, value, onChange }: {
  id: string
  ariaLabel: string
  value: string
  onChange: (value: string) => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    const resize = () => {
      element.style.height = 'auto'
      element.style.height = `${element.scrollHeight}px`
    }

    resize()

    // Without this a layout change rewraps the text taller while the pixel height stays put.
    const observer = new ResizeObserver(resize)
    observer.observe(element)
    return () => observer.disconnect()
  }, [value])

  return (
    <Textarea
      ref={ref}
      id={id}
      data-testid={id}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={1}
      className="min-h-0 resize-none overflow-hidden bg-background text-sm leading-relaxed"
    />
  )
}

function FieldEditorLabel({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  const className = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'
  return htmlFor
    ? <label htmlFor={htmlFor} className={className}>{children}</label>
    : <div className={className}>{children}</div>
}

function ChoiceFieldEditor({ fieldKey, label, options, value, onChange }: {
  fieldKey: string
  label: string
  options: readonly string[]
  value: string
  onChange: (value: string) => void
}) {
  // Keep an unrecognised stored value selectable rather than showing an empty placeholder.
  const offered = value && !options.includes(value) ? [value, ...options] : options

  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        id={`proposed-${fieldKey}-input`}
        data-testid={`proposed-${fieldKey}-input`}
        aria-label={`Proposed ${label}`}
        className="bg-background text-sm"
      >
        <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent>
        {offered.map((option) => (
          <SelectItem key={option} value={option}>{option}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function StepsFieldEditor({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const steps = parseSteps(value)

  if (steps === null) {
    return (
      <div className="rounded-md border px-3 py-2 text-sm italic text-danger-foreground" data-testid="proposed-steps-unreadable">
        Steps could not be read, so they cannot be edited for this item.
      </div>
    )
  }

  const write = (next: StepDiffItem[]) => {
    onChange(JSON.stringify(next.map((s, i) => ({
      ...s.raw,
      stepNumber: i + 1,
      description: s.description,
      expectedResult: s.expectedResult,
    }))))
  }

  const update = (index: number, patch: Partial<StepDiffItem>) =>
    write(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)))

  return (
    <div className="overflow-hidden rounded-md border" data-testid="proposed-steps-editor">
      <Table aria-label="Steps">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead>Step Name</TableHead>
            <TableHead>Expected Result</TableHead>
            <TableHead className="w-[64px]"><span className="sr-only">Actions</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {steps.map((step, index) => (
            <TableRow key={index}>
              <TableCell className="align-top text-muted-foreground">{index + 1}</TableCell>
              <TableCell className="align-top whitespace-normal">
                <AutoSizingTextarea
                  id={`proposed-step-${index + 1}-description-input`}
                  ariaLabel={`Step ${index + 1} name`}
                  value={step.description}
                  onChange={(description) => update(index, { description })}
                />
              </TableCell>
              <TableCell className="align-top whitespace-normal">
                <AutoSizingTextarea
                  id={`proposed-step-${index + 1}-expected-result-input`}
                  ariaLabel={`Step ${index + 1} expected result`}
                  value={step.expectedResult ?? ''}
                  onChange={(expectedResult) => update(index, { expectedResult })}
                />
              </TableCell>
              <TableCell className="align-top">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove step ${index + 1}`}
                  className="h-7 px-2 text-danger-foreground"
                  onClick={() => write(steps.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="border-t p-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => write([...steps, { stepNumber: steps.length + 1, description: '', expectedResult: null, raw: {} }])}
        >
          <Plus className="mr-1 h-3 w-3" />
          Add step
        </Button>
      </div>
    </div>
  )
}

function EditableFieldRow({ field, entityKind, view, isCreate, value, baseline, onChange, rawPreview, errorMessage }: {
  field: { key: string; before: string | null; after: string | null }
  entityKind: ChangeSetEntityKind
  view: DiffView
  isCreate: boolean
  value: string
  baseline: string
  onChange: (value: string) => void
  rawPreview: 'raw' | 'preview'
  errorMessage?: string
}) {
  const label = field.key.replace(/([A-Z])/g, ' $1')
  const testId = `change-set-field-${field.key}`
  const inputId = `proposed-${field.key}-input`
  const choices = choicesFor(entityKind, field.key)
  // From the seeded value, not the live one: switching component type mid-edit loses focus.
  const multiline = MULTILINE_FIELDS.has(field.key) || baseline.includes('\n')

  // The steps table needs the full width, so it never sits in the two-column layout.
  if (entityKind === 'TestCase' && field.key === 'steps') {
    return (
      <div className="mt-3" data-testid={testId}>
        <FieldEditorLabel>Steps</FieldEditorLabel>
        <StepsFieldEditor value={value} onChange={onChange} />
        <FieldError>{errorMessage}</FieldError>
      </div>
    )
  }

  if (MARKDOWN_FIELDS.has(field.key) && rawPreview === 'preview' && view === 'final') {
    return (
      <div className="mt-3" data-testid={testId}>
        <FieldEditorLabel>{label}</FieldEditorLabel>
        <div className="rounded-md border px-3 py-2 text-sm leading-relaxed">
          <MarkdownViewer content={value} bodyTextClass="text-foreground" />
        </div>
        <FieldError>{errorMessage}</FieldError>
      </div>
    )
  }

  const editor = choices
    ? <ChoiceFieldEditor fieldKey={field.key} label={label.trim()} options={choices} value={value} onChange={onChange} />
    : multiline
      ? <AutoSizingTextarea id={inputId} ariaLabel={`Proposed ${label.trim()}`} value={value} onChange={onChange} />
      : (
        <Input
          id={inputId}
          data-testid={inputId}
          aria-label={`Proposed ${label.trim()}`}
          aria-invalid={!!errorMessage}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-background text-sm"
        />
      )

  if (view === 'split') {
    return (
      <div className="mt-3" data-testid={testId}>
        <FieldEditorLabel htmlFor={inputId}>{label}</FieldEditorLabel>
        <div className="grid grid-cols-2 overflow-hidden rounded-md border">
          <div className="border-r bg-muted px-3 py-2">
            <div className="mb-1 text-[11px] font-bold tracking-wider">Current *</div>
            <div className="text-sm leading-relaxed">
              {isCreate || field.before === null
                ? <span className="italic text-muted-foreground">Does not exist</span>
                : <span className="whitespace-pre-wrap break-words">{field.before}</span>}
            </div>
          </div>
          <div className="bg-success px-3 py-2">
            <div className="mb-1 text-[11px] font-bold tracking-wider">Proposed *</div>
            {editor}
          </div>
        </div>
        <FieldError>{errorMessage}</FieldError>
      </div>
    )
  }

  return (
    <div className="mt-3" data-testid={testId}>
      <FieldEditorLabel htmlFor={inputId}>{label}</FieldEditorLabel>
      {editor}
      <FieldError>{errorMessage}</FieldError>
    </div>
  )
}

function ChangeSetItemTitleLabel({ title, discarded }: { title: string; discarded: boolean }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
  }, [title])

  const label = (
    <span ref={ref} className={cn('flex-1 truncate font-medium', discarded && 'line-through')}>
      {title}
    </span>
  )

  if (!isTruncated) return label

  return (
    <Tooltip>
      <TooltipTrigger asChild>{label}</TooltipTrigger>
      <TooltipContent className="max-w-xs break-words">{title}</TooltipContent>
    </Tooltip>
  )
}

export function ChangeSetDiffDialog({
  open,
  onOpenChange,
  changeSet,
  initialItemId,
  canDecide,
  canEdit,
  onSetDecision,
  onSaveProposedContent,
  isSaving,
}: Readonly<ChangeSetDiffDialogProps>) {
  const [view, setView] = useState<DiffView>(
    (changeSet.items.find((i) => i.id === initialItemId) ?? changeSet.items[0])?.entityKind === 'Document'
      ? 'final'
      : 'split',
  )
  const [selectedItemId, setSelectedItemId] = useState<string | null>(initialItemId ?? null)
  const [editing, setEditing] = useState(false)
  const [rawPreview, setRawPreview] = useState<'raw' | 'preview'>('raw')
  const [draft, setDraft] = useState<Record<string, string>>({})
  // Dirtiness is measured against this, not the live query data, which can refetch mid-edit.
  const [baseline, setBaseline] = useState<Record<string, string>>({})
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({})
  const [bannerError, setBannerError] = useState<string | null>(null)

  const selectedItem = changeSet.items.find((i) => i.id === selectedItemId) ?? changeSet.items[0]

  if (!selectedItem) return null

  const fields = orderedFields(selectedItem)
  const isCreate = selectedItem.operation === 'Create'

  const proposedValues = parseValues(selectedItem.proposedValuesJson)
  const editableFields = fields.filter((f) => isEditableField(selectedItem.entityKind, f.key))
  const editAvailable = canEdit && view !== 'unified' && editableFields.length > 0

  const hasMarkdownField = editableFields.some((f) => MARKDOWN_FIELDS.has(f.key))
  const rawPreviewAvailable = editing && view === 'final' && hasMarkdownField


  const changedKeys = editableFields
    .map((f) => f.key)
    .filter((key) => key in draft && draft[key] !== (baseline[key] ?? ''))
  const isDirty = editing && changedKeys.length > 0

  const conflictedKeys = changedKeys.filter((key) => (proposedValues[key] ?? '') !== (baseline[key] ?? ''))

  // Only changed fields are validated, matching the server: an untouched field the AI left
  // blank would otherwise disable Save on an edit the API would have accepted.
  const requiredFields = REQUIRED_FIELDS[selectedItem.entityKind] ?? new Set<string>()
  const blankRequiredField = changedKeys
    .find((key) => requiredFields.has(key) && (draft[key] ?? '').trim() === '')
  const stepsDraft = changedKeys.includes('steps') ? parseSteps(draft['steps']) : null
  const stepsProblem = stepsDraft === null
    ? undefined
    : stepsDraft.length === 0
      ? 'Add at least one step.'
      : stepsDraft.some((s) => s.description.trim() === '')
        ? 'Every step needs a step name.'
        : undefined
  const clientFieldErrors: Record<string, string> = {}
  if (blankRequiredField) {
    clientFieldErrors[blankRequiredField] = `${blankRequiredField.replace(/([A-Z])/g, ' $1')} is required.`
  }
  if (stepsProblem) {
    clientFieldErrors['steps'] = stepsProblem
  }
  const fieldErrors = { ...serverFieldErrors, ...clientFieldErrors }
  const hasBlockingError = Object.keys(clientFieldErrors).length > 0

  const exitEdit = () => {
    setEditing(false)
    setDraft({})
    setBaseline({})
    setRawPreview('raw')
    setServerFieldErrors({})
    setBannerError(null)
  }

  const startEdit = () => {
    const seeded = Object.fromEntries(editableFields.map((f) => [f.key, proposedValues[f.key] ?? '']))
    setBaseline(seeded)
    setDraft(seeded)
    setEditing(true)
    setRawPreview('raw')
    setServerFieldErrors({})
  }

  const decisionMutationOptions = {
    onSuccess: () => setBannerError(null),
    onError: (error: unknown) =>
      setBannerError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Could not update this item. Please try again.'),
  }

  const guardUnsaved = (proceed: () => void) => {
    if (isDirty) {
      setBannerError(UNSAVED_CHANGES_WARNING)
      return
    }
    proceed()
  }

  const handleSelectItem = (itemId: string) => {
    if (itemId === selectedItem.id) return
    guardUnsaved(() => {
      exitEdit()
      setSelectedItemId(itemId)
    })
  }

  const handleSetView = (nextView: DiffView) => {
    if (nextView === view) return
    setRawPreview('raw')
    // Side by side and Final item both support editing, so the draft survives between them;
    // Unified does not, so leaving for it ends the session.
    if (editing && nextView === 'unified') {
      guardUnsaved(() => {
        exitEdit()
        setView(nextView)
      })
      return
    }
    setView(nextView)
  }

  const handleSave = () => {
    if (conflictedKeys.length > 0) {
      setBannerError('This item changed while you were editing. Cancel and reopen it to see the latest content.')
      return
    }

    // Only changed keys are overwritten, so untouched nulls stay null.
    const next: Record<string, string | null> = { ...proposedValues }
    for (const key of changedKeys) {
      next[key] = draft[key]
    }
    onSaveProposedContent(selectedItem.id, JSON.stringify(next), {
      onSuccess: exitEdit,
      onError: (error) => {
        const messages: Record<string, string> = {}
        for (const key of changedKeys) {
          const message = getValidationErrorMessage(error, key)
          if (message) messages[key] = message
        }
        if (Object.keys(messages).length > 0) {
          setServerFieldErrors(messages)
        } else {
          setBannerError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Could not save changes. Please try again.')
        }
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : guardUnsaved(() => onOpenChange(false)))}>
      <DialogContent className="flex h-[80vh] max-w-5xl flex-col gap-0 p-0">
        {bannerError && (
          <div
            className="flex items-start gap-2 border-b border-warning-foreground bg-warning px-4 py-2"
            role="alert"
            data-testid="change-set-error-banner"
          >
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning-foreground" />
            <p className="flex-1 text-sm text-warning-foreground">{bannerError}</p>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-warning-foreground hover:text-warning-foreground"
              onClick={() => setBannerError(null)}
              aria-label="Dismiss error"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        <DialogHeader className="border-b px-4 py-3">
          <div className="flex items-center gap-2 pr-8">
            <DialogTitle className="text-lg font-semibold">Diff Inspector</DialogTitle>
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <Badge variant="outline">Round {changeSet.round}</Badge>
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
              changeSet.status === 'Pending' && 'bg-warning text-warning-foreground border border-warning-foreground',
              changeSet.status === 'Applied' && 'bg-success text-success-foreground border border-success-foreground',
              changeSet.status === 'Superseded' && 'bg-neutral text-neutral-foreground border border-neutral-foreground',
              changeSet.status === 'Discarded' && 'bg-danger text-danger-foreground border border-danger-foreground',
            )}>
              {changeSet.status === 'Pending' && <TriangleAlert className="h-2.5 w-2.5" />}
              {changeSet.status === 'Applied' && <Check className="h-2.5 w-2.5" />}
              {changeSet.status === 'Superseded' && <History className="h-2.5 w-2.5" />}
              {changeSet.status === 'Discarded' && <X className="h-2.5 w-2.5" />}
              {changeSet.status === 'Pending' ? 'Review required' : changeSet.status}
            </span>
          </div>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 grid-cols-[260px_1fr] gap-3 p-4">
          <aside className="self-start max-h-full overflow-y-auto rounded-lg border p-2">
            <div className="px-1 pb-1 mb-2 text-[12px] font-bold  tracking-wide border-b">
              Changes
            </div>
            <TooltipProvider>
            <div className="flex flex-col gap-2">
              {changeSet.items.map((item) => (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  aria-label={changeSetItemTitle(item)}
                  aria-current={item.id === selectedItem.id}
                  onClick={() => handleSelectItem(item.id)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return
                    e.preventDefault()
                    handleSelectItem(item.id)
                  }}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs',
                    item.id === selectedItem.id
                      ? 'bg-primary text-primary-foreground'
                      : 'border hover:border-info-foreground hover:bg-background',
                    item.decision === 'Discarded' && 'opacity-55',
                  )}
                >
                  {item.operation === 'Create'
                    ? <div className="bg-success border border-success-foreground rounded-full p-1"><CircleCheck className="h-3 w-3 shrink-0 text-success-foreground" /></div>
                    : <div className="bg-warning border border-warning-foreground rounded-full p-1"><TriangleAlert className="h-3.5 w-3.5 shrink-0 rounded-full text-warning-foreground" /></div>}
                  <ChangeSetItemTitleLabel title={changeSetItemTitle(item)} discarded={item.decision === 'Discarded'} />
                  {canDecide && (
                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Stage ${changeSetItemTitle(item)}`}
                        className={cn(
                          'group h-5 w-5 text-success-foreground hover:text-success-foreground',
                          item.decision === 'Staged' ? 'bg-success/40 hover:bg-success/40' : 'hover:bg-transparent',
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          guardUnsaved(() => {
                            onSetDecision(item.id, 'Staged', decisionMutationOptions)
                          })
                        }}
                      >
                        <Check className="h-3.5 w-3.5 transition-transform duration-150 group-hover:scale-125 group-active:scale-90" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Discard ${changeSetItemTitle(item)}`}
                        className={cn(
                          'group h-5 w-5 text-danger-foreground hover:text-danger-foreground',
                          item.decision === 'Discarded' ? 'bg-danger/40 hover:bg-danger/40' : 'hover:bg-transparent',
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          guardUnsaved(() => {
                            onSetDecision(item.id, 'Discarded', decisionMutationOptions)
                          })
                        }}
                      >
                        <X className="h-3.5 w-3.5 transition-transform duration-150 group-hover:scale-125 group-active:scale-90" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            </TooltipProvider>
          </aside>

          <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border">
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="flex flex-wrap items-center gap-2 border-b pb-3">
                
                <span className="text-sm font-semibold">
                  {editing ? `Edit ${changeSetItemTitle(selectedItem)}` : changeSetItemTitle(selectedItem)}
                </span>
                <span className="rounded-full border gap-1 px-2 py-0.5 text-[10px] font-semibold flex flex-row items-center">
                  {ENTITY_KIND_LABELS[selectedItem.entityKind] ?? selectedItem.entityKind}
                </span>
                {changeSet.round > 1 && selectedItem.revision !== 'Unchanged' && (
                  <span className="rounded-full bg-warning px-2 py-0.5 text-[9px] font-semibold uppercase text-warning-foreground">
                    {selectedItem.revision === 'New' ? 'new this round' : 'revised'}
                  </span>
                )}
                {!editing && editAvailable && (
                  <div className="ml-auto flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Edit proposed content"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                      onClick={startEdit}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="mt-3 flex w-fit gap-1 rounded-xl border bg-panel p-1">
                {([['split', 'Side by side'], ['unified', 'Unified'], ['final', 'Final item']] as const).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={view === v}
                    onClick={() => handleSetView(v)}
                    className={cn(
                      'rounded-md px-3 py-1 text-xs font-medium',
                      view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {selectedItem.decision === 'Discarded' && changeSet.status === 'Pending' && (
                <p className="mt-2 text-xs italic text-danger-foreground">
                  This item is discarded — it will be skipped when the gate is approved.
                </p>
              )}

              {editing && rawPreviewAvailable && (
                <div className="flex items-center justify-end gap-2 mt-3">
                  <Switch
                    id="markdown-preview-toggle"
                    checked={rawPreview === 'preview'}
                    onCheckedChange={(checked) => setRawPreview(checked ? 'preview' : 'raw')}
                    className="data-[state=unchecked]:bg-muted-foreground"
                  />
                  <label htmlFor="markdown-preview-toggle" className="text-xs font-medium text-foreground">
                    Markdown preview
                  </label>
                </div>
              )}

              {fields.map((field) => {
                // AC2: status stays read-only and is absent from the edit modal.
                if (editing && field.key === 'status') return null

                if (editing && isEditableField(selectedItem.entityKind, field.key))
                  return (
                    <EditableFieldRow
                      key={field.key}
                      field={field}
                      entityKind={selectedItem.entityKind}
                      view={view}
                      isCreate={isCreate}
                      value={draft[field.key] ?? ''}
                      baseline={baseline[field.key] ?? ''}
                      rawPreview={rawPreview}
                      errorMessage={fieldErrors[field.key]}
                      onChange={(value) => {
                        setDraft((current) => ({ ...current, [field.key]: value }))
                        setServerFieldErrors((current) => {
                          if (!(field.key in current)) return current
                          return Object.fromEntries(Object.entries(current).filter(([key]) => key !== field.key))
                        })
                      }}
                    />
                  )

                if (field.key === 'steps' && selectedItem.entityKind === 'TestCase')
                  return <TestStepsFieldDiff key={field.key} value={field.after} />

                if (isDocumentContent(selectedItem, field.key) && view === 'final')
                  return <DocumentContentDiff key={field.key} value={field.after} />

                return <FieldDiff key={field.key} field={field} view={view} isCreate={isCreate} />
              })}
            </div>

            {editing && (
              <div className="flex items-center justify-end gap-4 border-t px-4 py-3" aria-live="polite" aria-busy={isSaving}>
                <Button variant="ghost" size="sm" className="h-8" onClick={exitEdit} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-8"
                  onClick={handleSave}
                  disabled={isSaving || !isDirty || hasBlockingError}
                >
                  <Check className="mr-1.5 h-4 w-4" />
                  {isSaving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            )}

            {!editing && canDecide && (
              <div className="flex items-center justify-end gap-4 border-t px-4 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 text-danger-foreground hover:text-danger-foreground',
                    selectedItem.decision === 'Discarded' && 'font-semibold',
                  )}
                  onClick={() => {
                    onSetDecision(selectedItem.id, 'Discarded', decisionMutationOptions)
                  }}
                >
                  <X className="mr-1.5 h-4 w-4" />
                  Discard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 text-success-foreground hover:text-success-foreground',
                    selectedItem.decision === 'Staged' && 'font-semibold',
                  )}
                  onClick={() => {
                    onSetDecision(selectedItem.id, 'Staged', decisionMutationOptions)
                  }}
                >
                  <Check className="mr-1.5 h-4 w-4" />
                  Stage
                </Button>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
